import { Avatar, Card, Col, Flex, Progress, Row, Space, Typography } from 'antd';
import { UserOutlined, WalletOutlined, RiseOutlined, FallOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Alert } from 'components/elements';
import { get2DecimalPointsWithCommas } from 'components/numberFormatDigits';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Chart from "react-apexcharts";
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from "react-redux";
import { generateRandomString } from "utils/helper";
import dayjs from 'dayjs';
import {
    getEmployeeGroupByGender,
    getEmployeeGroupByType,
    getEmployeeGroupInfoByCompany,
    getEmployeeSalaryByBranch,
    getNewEmployee,
    getSeparationPage,
    getPayrollPeriodList,
    exportHrDashboardExcel,
} from '../service/genderApi';
import {
    DASHBOARD_SLICE,
    setEmployeeGroupByGender,
    setEmployeeGroupByType,
    setEmployeeGroupInfoByCompany,
    setEmployeeSalaryByBranch,
    setIsLoading,
    setNewEmployee,
    setPayrollPeriodYearRaw,
    setPayrollPeriodYearRawPrevYear,
} from '../slices/dashboardSlice';
import { useLocation, useNavigate } from 'react-router-dom';
import useOptions from "features/masterData/hooks/useOptions";
function useDashboard() {
    const navigate = useNavigate();

    const { employeeStatusTypeOptions
        , employeeTypeOptions
    } = useSelector((state) => state?.options);
    const onClickCustomer = useCallback((item) => {
        navigate("/employee", { state: { search: "dashboard", item } })
    }, [navigate]);

    const { getEmployeeStstuaTypeOptions } = useOptions();
    const { getEmployeeTypeOptions } = useOptions();
    const dispatch = useDispatch();
    const { employeeGroupByGender
        , employeeGroupByType
        , employeeGroupInfoByCompany
        , isLoading
        , employeeSalaryByBranch,
        newEmployee,
    } = useSelector((state) => state?.[DASHBOARD_SLICE]);


    useEffect(() => {
        getEmployeeStstuaTypeOptions()
    }, [])



    const { currentCompany, currentEmployee } = useSelector((state) => state.company);
    const { t, i18n } = useTranslation();
    const [pagination, setPagination] = useState({ page: 0, size: 10, pageSize: 10 });

    const [dataSource, setDataSource] = useState([])
    useEffect(() => {
        if (currentCompany != null) {
            getEmployeeTypeOptions({
                companyId: currentCompany,
                groupType: "EMPLOYEE_TYPE",
            });
        }
    }, [currentCompany, getEmployeeTypeOptions]);

    const getSeparationPageFn = useCallback(async (page, fn = () => { }) => {
        try {
            dispatch(setIsLoading(true));
            const response = await getSeparationPage({
                ...page
            });
            fn(response.data?.page?.content)
            setPagination({
                ...pagination,
                total: response.data?.page?.totalElements,
            });
        } catch (error) {
            Alert({ type: "error", resultObject: error });
        } finally {
            dispatch(setIsLoading(false));
        }
    }, [dataSource]);

    useEffect(() => {
        getSeparationPageFn(pagination, setDataSource)
    }, [])

    const newEmployeeCb = useCallback(async () => {
        try {
            dispatch(setIsLoading(true));
            const response = await getNewEmployee({
                companyId: currentCompany,
                requestId: generateRandomString(),
            });
            dispatch(setNewEmployee(response?.data))

        } catch (error) {
            console.error("error", error);
        } finally {
            dispatch(setIsLoading(false));
        }
    }, [dispatch, currentCompany])

    const EMPLOYEE_STATUS_CODE = {
        PROBATION: 'EMPLOYEE_STATUS_TYPE_01',
        PERMANENT: 'EMPLOYEE_STATUS_TYPE_02',
        RESIGNED: 'EMPLOYEE_STATUS_TYPE_03',
        PENDING_START: 'EMPLOYEE_STATUS_TYPE_04',
    };

    const getStatusIdsByCode = useCallback(
        (codes) => (employeeStatusTypeOptions ?? [])
            .filter((o) => codes.includes(o.code))
            .map((o) => o.id),
        [employeeStatusTypeOptions]
    );


    const NATIONALITY_COLORS = ["#3B82F6", "#24E08F", "#FFA152", "#AF58FF", "#FF50AD", "#FF5858"];
    const dataNationality = useMemo(() => {
        const list = employeeGroupInfoByCompany?.nationalityList ?? [];
        return [...list]
            .sort((a, b) => b.count - a.count)
            .map((o, i) => ({
                name: o.nationality?.name ?? t('common.notSpecified'),
                nameEn: o.nationality?.nameEn ?? t('common.notSpecified'),
                count: o.count,
                color: NATIONALITY_COLORS[i % NATIONALITY_COLORS.length],
            }));
    }, [employeeGroupInfoByCompany, i18n.language]);

    const AGE_COLORS = {
        'ต่ำกว่า 20 ปี': '#3B82F6',
        '20 - 29 ปี': '#24E08F',
        '30 - 39 ปี': '#FFA152',
        '40 - 49 ปี': '#AF58FF',
        '50 ปีขึ้นไป': '#FF5A9E',
    };

    const AGE_LABELS = {
        'ต่ำกว่า 20 ปี': t('dashboard.age.under20'),
        '20 - 29 ปี': t('dashboard.age.20to29'),
        '30 - 39 ปี': t('dashboard.age.30to39'),
        '40 - 49 ปี': t('dashboard.age.40to49'),
        '50 ปีขึ้นไป': t('dashboard.age.50plus'),
    };
    const dataAgeRange = useMemo(() => {
        return employeeGroupInfoByCompany?.ageList ?? [];
    }, [employeeGroupInfoByCompany]);

    const dataAgeRangeMapped = useMemo(() => {
        return dataAgeRange.map((o) => ({
            name: AGE_LABELS[o.label] ?? o.label,
            nameEn: AGE_LABELS[o.label] ?? o.label,
            count: o.count,
            color: AGE_COLORS[o.label] ?? '#8c8c8c',
        }));
    }, [dataAgeRange, i18n.language]);
    const AgeRangeChart = () => {
        const totalAge = dataAgeRangeMapped?.reduce((p, c) => p + Number(c.count || 0), 0) ?? 0;
        const totalGen = dataGeneration?.reduce((p, c) => p + Number(c.count || 0), 0) ?? 0;

        return (
            <Card bodyStyle={{ backgroundColor: "transparent", padding: 20 }} bordered={false} style={{ height: '100%', width: "100%" }}>
                <Row gutter={[16, 16]} align="top">
                    <Col md={24} lg={12}>
                        <AgeDonutWithLegend
                            items={dataAgeRangeMapped}
                            total={totalAge}
                            totalLabel={t('dashboard.label.total_employee', t('totalEmployee'))}
                            t={t}
                            i18n={i18n}
                        />
                    </Col>
                    {dataGeneration.length > 0 && (
                        <Col md={24} lg={12}>
                            <AgeDonutWithLegend
                                items={dataGeneration}
                                total={totalGen}
                                totalLabel={t('dashboard.label.total_employee', t('totalEmployee'))}
                                t={t}
                                i18n={i18n}
                            />
                        </Col>
                    )}
                </Row>
            </Card>
        );
    };
    const dataPie = useMemo(() => {
        let sortable = [...employeeGroupByGender?.map(o => {
            return {
                ...o,
                type: typeGender(o.type)?.["name" + (i18n.language == 'en' ? "En" : "")] || "",
                typeEn: typeGender(o.type)?.["name" + (i18n.language == 'en' ? "En" : "")] || "",
                color: typeGender(o.type).color,
                icon: typeGender(o.type)?.icon || (() => { })
            }
        })];
        function compare(a, b) {
            if (b?.type == 'ไม่ระบุเพศ' || a?.type == 'ไม่ระบุเพศ') {
                if (b?.type == 'ไม่ระบุเพศ')
                    return -1;
                if (a?.type == 'ไม่ระบุเพศ')
                    return 1;
            }
            if (a.type < b.type)
                return -1;
            if (a.type > b.type)
                return 1;
            return 0;
        }
        sortable?.sort(compare)
        return sortable
    }, [employeeGroupByGender, i18n.language])

    const dataPieMapped = useMemo(() => {
        return dataPie?.map(o => ({
            name: o.type,
            nameEn: o.typeEn,
            count: o.count,
            color: o.color,
        })) ?? [];
    }, [dataPie]);
    const dataPieType = useMemo(() => {
        let sortable = [...employeeGroupByType];
        function compare(a, b) {
            if (b?.type == 'null' || a?.type == 'null') {
                if (b?.type == 'null')
                    return -1;
                if (a?.type == 'null')
                    return 1;
            }
            if (a.type < b.type)
                return -1;
            if (a.type > b.type)
                return 1;
            return 0;
        }
        sortable?.sort(compare)
        return sortable
    }, [employeeGroupByType])
    const EMPLOYEE_TYPE_COLORS = ["#FFA152", "#24E08F", "#AF58FF", "#FF50AD", "#47B2FF", "#FF5858", "#FFD65C"];

    const dataPieTypeMapped = useMemo(() => {
        const countMap = new Map((dataPieType ?? []).map((o) => [o.id, o]));
        const merged = (employeeTypeOptions ?? []).map((master) => {
            const matched = countMap.get(master.id);
            return {
                id: master.id,
                name: master.name ?? t('common.notSpecified'),
                nameEn: master.nameEn ?? t('common.notSpecified'),
                count: matched?.count ?? 0,
            };
        });
        return merged
            .sort((a, b) => b.count - a.count)
            .map((o, i) => ({
                ...o,
                color: EMPLOYEE_TYPE_COLORS[i % EMPLOYEE_TYPE_COLORS.length],
            }));
    }, [dataPieType, employeeTypeOptions, i18n.language, t]);
    const dataColumnSalary = useMemo(() => {
        let sortable = [...employeeSalaryByBranch].filter(o => o?.branch?.branchCode != null);
        function compare(a, b) {
            if (Number(a.count) < Number(b.count))
                return 1;
            if (Number(a.count) > Number(b.count))
                return -1;
            return 0;
        }
        sortable?.sort(compare)

        return sortable
    }, [employeeSalaryByBranch])
    const BRANCH_COLORS = ["#3B82F6", "#24E08F", "#FFA152", "#AF58FF", "#FF50AD"];
    const DEPARTMENT_COLORS = ["#AF58FF", "#3B82F6", "#24E08F", "#FFA152", "#FF50AD", "#FF5858", "#FFD65C"];

    const dataBranchBreakdown = useMemo(() => {
        const list = employeeGroupInfoByCompany?.branch ?? [];
        return [...list]
            .sort((a, b) => b.count - a.count)
            .map((o, i) => ({
                id: o.branch?.uid,
                name: o.branch?.name ?? t('common.notSpecified'),
                nameEn: o.branch?.nameEn ?? t('common.notSpecified'),
                count: o.count,
                color: BRANCH_COLORS[i % BRANCH_COLORS.length],
            }));
    }, [employeeGroupInfoByCompany, i18n.language]);
    const TENURE_COLORS = [
        "#6C63FF",
        "#22A6F2",
        "#20C997",
        "#FFA726",
    ];

    // const dataTenure = useMemo(() => {
    //     const now = dayjs();

    //     const groups = [
    //         { key: 'underOneYear', count: 0, color: "#6C63FF" },
    //         { key: 'oneToFiveYears', count: 0, color: "#22A6F2" },
    //         { key: 'sixToTenYearsOld', count: 0, color: "#20C997" },
    //         { key: 'moreThanTenYears', count: 0, color: "#FFA726" },
    //     ];

    //     (employeeGroupInfoByCompany?.dateofwork ?? [])
    //         .filter(item => !item.dateExpiration) // เอาเฉพาะพนักงานที่ยังไม่ลาออก
    //         .forEach((item) => {
    //             if (!item.employmentDate) return;

    //             const years = now.diff(dayjs(item.employmentDate), "year", true);

    //             if (years < 1) {
    //                 groups[0].count++;
    //             } else if (years <= 5) {
    //                 groups[1].count++;
    //             } else if (years <= 10) {
    //                 groups[2].count++;
    //             } else {
    //                 groups[3].count++;
    //             }
    //         });

    //     return groups;
    // }, [employeeGroupInfoByCompany]);

    const dataDepartmentBreakdown = useMemo(() => {
        const list = employeeGroupInfoByCompany?.department ?? [];
        return [...list]
            .sort((a, b) => b.count - a.count)
            .map((o, i) => ({
                id: o.department?.uid,
                name: o.department?.name ?? t('common.notSpecified'),
                nameEn: o.department?.nameEn ?? t('common.notSpecified'),
                count: o.count,
                color: DEPARTMENT_COLORS[i % DEPARTMENT_COLORS.length],
            }));
    }, [employeeGroupInfoByCompany, i18n.language]);

    // NEW: derive เข้า-ออก data straight from employeeGroupInfoByCompany.dateofwork
    // (employmentDate = วันเริ่มงาน, dateExpiration = วันสิ้นสุด/ลาออก) — no extra API call needed
    const dataofwork = useMemo(
        () => employeeGroupInfoByCompany?.dateofwork ?? [],
        [employeeGroupInfoByCompany]
    );

    const TH_MONTHS_SHORT = [t('Jan'), t('Feb'), t('mar'), t('apr'), t('may'), t('jun'), t('jul'), t('aug'), t('sep'), t('oct'), t('nov'), t('dec')];
    const CURRENT_BE_YEAR = dayjs().year() + 543;

    // ตัวกรอง: ช่วงเวลา (3/6/12 เดือน) + ปี (พ.ศ.)
    const [turnoverPeriodMonths, setTurnoverPeriodMonths] = useState(1);
    const [turnoverYear, setTurnoverYear] = useState(CURRENT_BE_YEAR);

    // รายการปีให้เลือก: ปีปัจจุบัน + ปีที่มีข้อมูลจริงใน dateofwork
    const turnoverYearOptions = useMemo(() => {
        const years = new Set([CURRENT_BE_YEAR]);
        dataofwork.forEach((o) => {
            if (o.employmentDate) years.add(dayjs(o.employmentDate).year() + 543);
            if (o.dateExpiration) years.add(dayjs(o.dateExpiration).year() + 543);
        });
        return Array.from(years).sort((a, b) => b - a);
    }, [dataofwork]);

    // เดือนสุดท้ายของช่วง: ถ้าเลือกปีปัจจุบัน ให้จบที่เดือนนี้, ถ้าเลือกปีอื่น ให้จบที่ธันวาคมของปีนั้น
    const turnoverEndMonth = useMemo(() => {
        if (turnoverYear === CURRENT_BE_YEAR) {
            return dayjs().startOf('month');
        }
        const gregorianYear = turnoverYear - 543;
        return dayjs(`${gregorianYear}-12-01`).startOf('month');
    }, [turnoverYear]);

    const turnoverStartMonth = useMemo(() => {
        const gregorianYear = turnoverYear - 543;
        return dayjs(`${gregorianYear}-01-01`).startOf('month');
    }, [turnoverYear]);

    const monthsRange = useMemo(() => {
        // ช่วงที่อยากได้ตาม period ที่เลือก (3/6/12 เดือน) นับถอยจาก endMonth
        const desiredStart = turnoverEndMonth.subtract(turnoverPeriodMonths - 1, 'month');
        // แต่ห้ามย้อนก่อน ม.ค. ของปีที่เลือก
        const start = desiredStart.isBefore(turnoverStartMonth) ? turnoverStartMonth : desiredStart;

        const totalMonths = turnoverEndMonth.diff(start, 'month') + 1;
        const arr = [];
        for (let i = 0; i < totalMonths; i++) {
            arr.push(start.add(i, 'month'));
        }
        return arr;
    }, [turnoverEndMonth, turnoverStartMonth, turnoverPeriodMonths]);

    const onClickNewHireCard = useCallback(() => {
        onClickCustomer({
            series: 'listEmployeeStatus',
            value: getStatusIdsByCode([
                EMPLOYEE_STATUS_CODE.PROBATION,
                EMPLOYEE_STATUS_CODE.PERMANENT,
                EMPLOYEE_STATUS_CODE.PENDING_START,
            ]),
            startDate: monthsRange[0]?.format('YYYY-MM-DD'),
            endDate: monthsRange[monthsRange.length - 1]?.endOf('month').format('YYYY-MM-DD'),
        });
    }, [onClickCustomer, getStatusIdsByCode, monthsRange]);

    const onClickResignedCard = useCallback(() => {
        onClickCustomer({
            series: 'listEmployeeStatus',
            value: getStatusIdsByCode([EMPLOYEE_STATUS_CODE.RESIGNED]),
            startDate: monthsRange[0]?.format('YYYY-MM-DD'),
            endDate: monthsRange[monthsRange.length - 1]?.endOf('month').format('YYYY-MM-DD'),
        });
    }, [onClickCustomer, getStatusIdsByCode, monthsRange]);

    const dataTurnoverTrend = useMemo(() => {
        return monthsRange.map((m) => {
            const endOfMonthMs = m.endOf('month').valueOf();
            const newHire = dataofwork.filter(
                (o) => o.employmentDate && dayjs(o.employmentDate).isSame(m, 'month')
            ).length;
            const leave = dataofwork.filter(
                (o) => o.dateExpiration && dayjs(o.dateExpiration).isSame(m, 'month')
            ).length;
            const totalActive = dataofwork.filter((o) => {
                if (!o.employmentDate) return false;
                const startedByThen = dayjs(o.employmentDate).valueOf() <= endOfMonthMs;
                const stillActive = !o.dateExpiration || dayjs(o.dateExpiration).valueOf() > endOfMonthMs;
                return startedByThen && stillActive;
            }).length;
            return {
                monthLabel: `${TH_MONTHS_SHORT[m.month()]}`,
                monthLabelEn: m.format('MMM'),
                newHire,
                leave,
                totalActive,
            };
        });
    }, [dataofwork, monthsRange], i18n.language);

    // นับจำนวนที่ employmentDate/dateExpiration ตกอยู่ในช่วง [start, end] (รวมทั้งสองปลาย)
    const countInRange = useCallback((field, start, end) => {
        const startMs = start.valueOf();
        const endMs = end.valueOf();
        return dataofwork.filter((o) => {
            if (!o[field]) return false;
            const t = dayjs(o[field]).valueOf();
            return t >= startMs && t <= endMs;
        }).length;
    }, [dataofwork]);
    const isActiveEmployee = useCallback((item) => {
        return !!item.employmentDate && (!item.dateExpiration || dayjs(item.dateExpiration).isAfter(dayjs()));
    }, []);
    // NEW: top stat numbers for the turnover card, + เทียบกับช่วงเดียวกันปีก่อน (%)
    const turnoverStats = useMemo(() => {
        const periodStart = monthsRange[0];
        const periodEnd = turnoverEndMonth.endOf('month');

        const newHireCount = countInRange('employmentDate', periodStart, periodEnd);
        const leaveCount = countInRange('dateExpiration', periodStart, periodEnd);

        const totalEmployeeCount = employeeGroupInfoByCompany.activeEmployeeCount;
        const retentionRate = totalEmployeeCount > 0
            ? ((totalEmployeeCount - leaveCount) / totalEmployeeCount) * 100
            : 0;

        // ช่วงเดียวกันของปีก่อนหน้า (ย้อนไป 1 ปี)
        const prevPeriodStart = periodStart.subtract(1, 'year');
        const prevPeriodEnd = periodEnd.subtract(1, 'year');
        const prevNewHireCount = countInRange('employmentDate', prevPeriodStart, prevPeriodEnd);
        const prevLeaveCount = countInRange('dateExpiration', prevPeriodStart, prevPeriodEnd);

        const pctChange = (curr, prev) => {
            if (prev === 0) return curr === 0 ? 0 : 100;
            return ((curr - prev) / prev) * 100;
        };

        return {
            newHireCount,
            leaveCount,
            retentionRate,
            newHireChangePct: pctChange(newHireCount, prevNewHireCount),
            leaveChangePct: pctChange(leaveCount, prevLeaveCount),
        };
    }, [dataofwork, monthsRange, turnoverEndMonth, countInRange]);
    const DistributionList = ({ data, showTotal = false }) => {
        const total = data.reduce((sum, item) => sum + item.count, 0);

        return (
            <>
                {showTotal && (
                    <div style={{ marginBottom: 20 }}>
                        <Typography.Text type="secondary">
                            พนักงานทั้งหมด
                        </Typography.Text>

                        <div
                            style={{
                                fontSize: 28,
                                fontWeight: 700
                            }}
                        >
                            {total} คน
                        </div>
                    </div>
                )}

                {data.map(item => (
                    <Row
                        key={item.name}
                        align="middle"
                        gutter={16}
                        style={{ marginBottom: 18 }}
                    >
                        <Col span={5}>
                            {item.name}
                        </Col>

                        <Col flex="auto">
                            <Progress
                                percent={(item.count / total) * 100}
                                showInfo={false}
                                strokeColor={item.color}
                            />
                        </Col>

                        <Col>
                            {item.count} คน ({((item.count / total) * 100).toFixed(2)}%)
                        </Col>
                    </Row>
                ))}
            </>
        );
    };
    // NEW: recently joined employees, most recent employmentDate first
    // Note: dateofwork does not include department/position — only name/date fields are available here.
    const dataRecentHires = useMemo(() => {
        return [...dataofwork]
            .filter((o) => o.employmentDate && isActiveEmployee(o))
            .sort((a, b) => dayjs(b.employmentDate).valueOf() - dayjs(a.employmentDate).valueOf());
    }, [dataofwork, isActiveEmployee]);

    function typeGender(params) {
        const menIcon = () => {
            return `<svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M32.5446 2.25H22.4196C22.2871 2.25 22.1786 2.35848 22.1786 2.49107V4.74107C22.1786 4.87366 22.2871 4.98214 22.4196 4.98214H28.8643L20.8567 12.9897C18.8478 11.4429 16.3969 10.6071 13.8214 10.6071C10.7317 10.6071 7.82277 11.8125 5.64107 13.9982C3.45937 16.1839 2.25 19.0888 2.25 22.1786C2.25 25.2683 3.45536 28.1772 5.64107 30.3589C7.82277 32.5446 10.7317 33.75 13.8214 33.75C16.9112 33.75 19.8201 32.5446 22.0018 30.3589C24.1875 28.1772 25.3929 25.2683 25.3929 22.1786C25.3929 19.6031 24.5571 17.1603 23.0143 15.1513L31.0179 7.14777V13.5804C31.0179 13.7129 31.1263 13.8214 31.2589 13.8214H33.5089C33.6415 13.8214 33.75 13.7129 33.75 13.5804V3.45536C33.75 2.79241 33.2076 2.25 32.5446 2.25ZM13.8214 30.6964C9.12455 30.6964 5.30357 26.8754 5.30357 22.1786C5.30357 17.4817 9.12455 13.6607 13.8214 13.6607C18.5183 13.6607 22.3393 17.4817 22.3393 22.1786C22.3393 26.8754 18.5183 30.6964 13.8214 30.6964Z" fill="#47B2FF" fill-opacity="0.85"/></svg>`
        }
        const femenIcon = () => {
            return `<svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g clip-path="url(#clip0_806_9388)">
                <path d="M26.0682 19.4786C28.2217 17.325 29.411 14.4563 29.411 11.4107C29.411 8.36116 28.2258 5.49643 26.0682 3.34286C23.9146 1.18929 21.0459 0 18.0003 0C14.9548 0 12.086 1.18527 9.93246 3.34286C7.77889 5.50045 6.5896 8.36116 6.5896 11.4107C6.5896 13.9741 7.42933 16.4089 8.97621 18.3978C9.26951 18.7754 9.58692 19.133 9.92844 19.4786C10.27 19.8201 10.6316 20.1375 11.0052 20.4308C12.5963 21.6683 14.4726 22.4558 16.4735 22.721V27H11.8932C11.7164 27 11.5717 27.1446 11.5717 27.3214V29.7321C11.5717 29.9089 11.7164 30.0536 11.8932 30.0536H16.4735V35.6786C16.4735 35.8554 16.6182 36 16.795 36H19.2057C19.3825 36 19.5271 35.8554 19.5271 35.6786V30.0536H24.1075C24.2842 30.0536 24.4289 29.9089 24.4289 29.7321V27.3214C24.4289 27.1446 24.2842 27 24.1075 27H19.5271V22.721C21.9981 22.3915 24.2762 21.2705 26.0682 19.4786ZM18.0003 19.7679C15.7664 19.7679 13.6731 18.9 12.09 17.321C10.511 15.742 9.64317 13.6446 9.64317 11.4107C9.64317 9.17679 10.511 7.08348 12.09 5.50045C13.6691 3.91741 15.7664 3.05357 18.0003 3.05357C20.2342 3.05357 22.3275 3.92143 23.9106 5.50045C25.4896 7.07946 26.3575 9.17679 26.3575 11.4107C26.3575 13.6446 25.4896 15.7379 23.9106 17.321C22.3275 18.9 20.2342 19.7679 18.0003 19.7679Z" fill="#FF50AD" fill-opacity="0.85"/>
                </g>
                <defs>
                <clipPath id="clip0_806_9388">
                <rect width="36" height="36" fill="#F9BEE9"/>
                </clipPath>
                </defs>
                </svg>`
        }
        const otherIcon = () => {
            return `<div style="margin-right: 10px"><svg width="26" height="26" viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="13" cy="13" r="13" fill="#FFD65C"/>
            </svg></div>
            `
        }
        switch (params) {
            case "Male":
                return { name: `ชาย`, nameEn: `Male`, color: '#47B2FF', icon: menIcon() }
            case "Female":
                return { name: `หญิง`, nameEn: `Female`, color: '#FF50AD', icon: femenIcon() }
            default:
                return { name: `ไม่ระบุเพศ`, nameEn: `Unspecified`, color: '#FFD65C', icon: otherIcon() }
        }
    }
    const { payrollPeriodYearRaw, payrollPeriodYearRawPrevYear } = useSelector((state) => state?.[DASHBOARD_SLICE]);

    const [salaryPeriodMonths, setSalaryPeriodMonths] = useState(12);
    const [salaryYear, setSalaryYear] = useState(CURRENT_BE_YEAR);

    // ดึงข้อมูลปีที่เลือก + ปีก่อนหน้า (เพื่อเทียบเส้นประ)
    const getPayrollPeriodYearFn = useCallback(async () => {
        try {
            dispatch(setIsLoading(true));
            const gregorianYear = salaryYear - 543;
            const [curRes, prevRes] = await Promise.all([
                getPayrollPeriodList({
                    companyId: currentCompany,
                    employeeId: currentEmployee,
                    actionId: currentEmployee,
                    year: gregorianYear,
                    requestId: generateRandomString(),
                }),
                getPayrollPeriodList({
                    companyId: currentCompany,
                    employeeId: currentEmployee,
                    actionId: currentEmployee,
                    year: gregorianYear - 1,
                    requestId: generateRandomString(),
                }),
            ]);
            dispatch(setPayrollPeriodYearRaw(Array.isArray(curRes.data?.content) ? curRes.data.content : []));
            dispatch(setPayrollPeriodYearRawPrevYear(Array.isArray(prevRes.data?.content) ? prevRes.data.content : []));
        } catch (error) {
            console.log("error", error);
        } finally {
            dispatch(setIsLoading(false));
        }
    }, [currentCompany, currentEmployee, salaryYear, dispatch]);
    function buildMonthMap(rows) {
        const map = {};
        const list = Array.isArray(rows) ? rows : [];
        list.forEach((o) => {
            const key = `${o.year}-${String(o.month).padStart(2, '0')}`;
            const totalPaid = Number(o.totalPaid) || 0;
            const estimatedTotalPaid = Number(o.estimatedTotalPaid) || 0;
            const employee = Number(o.employee) || 0;
            const estimatedEmployee = Number(o.estimatedEmployee) || 0;
            const useEstimated = totalPaid === 0 && estimatedTotalPaid > 0;

            map[key] = {
                employee: useEstimated ? estimatedEmployee : employee,
                totalPaid: useEstimated ? estimatedTotalPaid : totalPaid,
            };
        });
        return map;
    }

    const monthlyMapCurrent = useMemo(
        () => buildMonthMap(payrollPeriodYearRaw),
        [payrollPeriodYearRaw]
    );
    const monthlyMapPrev = useMemo(
        () => buildMonthMap(payrollPeriodYearRawPrevYear),
        [payrollPeriodYearRawPrevYear]
    );
    useEffect(() => {
        if (currentCompany) {
            getPayrollPeriodYearFn();
        }
    }, [currentCompany, salaryYear]);





    // ช่วงเวลา rolling (ไม่ clip ที่ ม.ค. เหมือนกราฟเข้า-ออก เพราะกราฟนี้ข้ามปีได้ตามภาพตัวอย่าง)
    const salaryStartMonth = useMemo(() => {
        const gregorianYear = salaryYear - 543;
        return dayjs(`${gregorianYear}-01-01`).startOf('month');
    }, [salaryYear]);

    const salaryYearEndMonth = useMemo(() => {
        const gregorianYear = salaryYear - 543;
        return dayjs(`${gregorianYear}-12-01`).startOf('month');
    }, [salaryYear]);

    const salaryMonthsRange = useMemo(() => {
        // 12 เดือน ให้ฟิกเป็นทั้งปี ม.ค.-ธ.ค. เสมอ ไม่ขึ้นกับเดือนปัจจุบัน
        if (salaryPeriodMonths === 12) {
            const arr = [];
            for (let i = 0; i < 12; i++) {
                arr.push(salaryStartMonth.add(i, 'month'));
            }
            return arr;
        }

        // 1/3/6 เดือน: เริ่มจากเดือนปัจจุบัน มองไปข้างหน้า (แทนการย้อนหลัง)
        const anchor = salaryYear === CURRENT_BE_YEAR ? dayjs().startOf('month') : salaryStartMonth;
        const clampedAnchor = anchor.isBefore(salaryStartMonth)
            ? salaryStartMonth
            : (anchor.isAfter(salaryYearEndMonth) ? salaryYearEndMonth : anchor);

        const desiredEnd = clampedAnchor.add(salaryPeriodMonths - 1, 'month');
        const end = desiredEnd.isAfter(salaryYearEndMonth) ? salaryYearEndMonth : desiredEnd;

        const totalMonths = end.diff(clampedAnchor, 'month') + 1;
        const arr = [];
        for (let i = 0; i < totalMonths; i++) {
            arr.push(clampedAnchor.add(i, 'month'));
        }
        return arr;
    }, [salaryPeriodMonths, salaryYear, salaryStartMonth, salaryYearEndMonth]);

    const dataSalaryTrend = useMemo(() => {
        return salaryMonthsRange.map((m) => {
            const curKey = `${m.year()}-${String(m.month() + 1).padStart(2, '0')}`;
            const prevM = m.subtract(1, 'year');
            const prevKey = `${prevM.year()}-${String(prevM.month() + 1).padStart(2, '0')}`;

            const cur = monthlyMapCurrent[curKey];
            const prev = monthlyMapPrev[prevKey];

            return {
                monthLabel: `${TH_MONTHS_SHORT[m.month()]}`,
                monthLabelEn: m.format('MMM YY'),
                currentYearTotal: cur ? cur.totalPaid : null,
                previousYearTotal: prev ? prev.totalPaid : null,
                currentYearAvg: cur && cur.employee > 0 ? Math.round(cur.totalPaid / cur.employee) : null,
                previousYearAvg: prev && prev.employee > 0 ? Math.round(prev.totalPaid / prev.employee) : null,
            };
        });
    }, [salaryMonthsRange, monthlyMapCurrent, monthlyMapPrev], i18n.language);

    // ช่วงของปีก่อนหน้า (ไว้ใช้แสดงในการ์ด "หมายเหตุ")
    const salaryPrevPeriodLabel = useMemo(() => {
        if (salaryMonthsRange.length === 0) return '';
        const firstPrev = salaryMonthsRange[0].subtract(1, 'year');
        const lastPrev = salaryMonthsRange[salaryMonthsRange.length - 1].subtract(1, 'year');
        const fmt = (m) => `${TH_MONTHS_SHORT[m.month()]} ${String(m.year() + 543).slice(-2)}`;
        return `${fmt(firstPrev)} - ${fmt(lastPrev)}`;
    }, [salaryMonthsRange]);

    // สถิติด้านล่างการ์ด: ค่าเฉลี่ยรวมทั้งช่วง + % เทียบปีก่อน
    const salaryStats = useMemo(() => {
        const sumCur = dataSalaryTrend.reduce((acc, o) => {
            if (o.currentYearAvg != null) { acc.total += o.currentYearAvg; acc.count += 1; }
            return acc;
        }, { total: 0, count: 0 });
        const sumPrev = dataSalaryTrend.reduce((acc, o) => {
            if (o.previousYearAvg != null) { acc.total += o.previousYearAvg; acc.count += 1; }
            return acc;
        }, { total: 0, count: 0 });

        const avgCur = sumCur.count > 0 ? sumCur.total / sumCur.count : 0;
        const avgPrev = sumPrev.count > 0 ? sumPrev.total / sumPrev.count : 0;
        const pctChange = avgPrev > 0 ? ((avgCur - avgPrev) / avgPrev) * 100 : (avgCur > 0 ? 100 : 0);

        return {
            avgPerPerson: avgCur,
            pctChange,
            diffAmount: avgCur - avgPrev,
        };
    }, [dataSalaryTrend]);

    // Component กราฟ
    const SalaryTrendChart = () => {
        const categories = dataSalaryTrend.map((o) => o.monthLabel);
        const curSeries = dataSalaryTrend.map((o) => o.currentYearTotal);
        const prevSeries = dataSalaryTrend.map((o) => o.previousYearTotal);

        // หา index ของจุดสุดท้ายที่มีข้อมูลจริง (ไม่ใช่ null) เพื่อวาง label ปลายเส้น
        const lastIndexWithValue = (arr) => {
            for (let i = arr.length - 1; i >= 0; i--) {
                if (arr[i] != null) return i;
            }
            return -1;
        };
        const lastCurIdx = lastIndexWithValue(curSeries);
        const lastPrevIdx = lastIndexWithValue(prevSeries);

        const annotationPoints = [];
        if (lastCurIdx !== -1) {
            annotationPoints.push({
                x: categories[lastCurIdx],
                y: curSeries[lastCurIdx],
                seriesIndex: 0,
                marker: { size: 0 },
                label: {
                    borderWidth: 0,
                    offsetY: -12,
                    style: {
                        background: 'transparent',
                        color: '#3B82F6',
                        fontSize: '14px',
                        fontWeight: 700,
                    },
                    text: get2DecimalPointsWithCommas(curSeries[lastCurIdx], 0),
                },
            });
        }
        if (lastPrevIdx !== -1) {
            annotationPoints.push({
                x: categories[lastPrevIdx],
                y: prevSeries[lastPrevIdx],
                seriesIndex: 1,
                marker: { size: 0 },
                label: {
                    borderWidth: 0,
                    offsetY: lastPrevIdx === lastCurIdx ? 16 : -12,
                    style: {
                        background: 'transparent',
                        color: '#FF5A9E',
                        fontSize: '14px',
                        fontWeight: 700,
                    },
                    text: get2DecimalPointsWithCommas(prevSeries[lastPrevIdx], 0),
                },
            });
        }
        const totalExpense = useMemo(
            () =>
                dataSalaryTrend
                    ?.map((o) => Number(o.currentYearTotal))
                    ?.filter((n) => !isNaN(n) && n !== null)
                    ?.reduce((p, c) => p + c, 0) ?? 0,
            [dataSalaryTrend]
        );

        const StatCard = ({ icon, bg, color, label, value, unit }) => (
            <Card bodyStyle={{ padding: '14px 16px' }} style={{ height: '100%' }}>
                <Flex align="center" gap={12}>
                    <Flex justify="center" align="center" style={{ width: 40, height: 40, borderRadius: '50%', background: bg, color, fontSize: 18 }}>
                        {icon}
                    </Flex>
                    <Flex vertical>
                        <span style={{ fontSize: 12, color: '#8c8c8c' }}>{label}</span>
                        <span style={{ fontSize: 18, fontWeight: 700, color }}>
                            {value} <span style={{ fontSize: 12, fontWeight: 400, color: '#263238' }}>{unit}</span>
                        </span>
                    </Flex>
                </Flex>
            </Card>
        );
        const options = {
            chart: { type: 'area', toolbar: { show: false } },
            stroke: { width: [3, 2], curve: 'smooth', dashArray: [0, 6] },
            colors: ['#3B82F6', '#FF5A9E'],
            fill: {
                type: ['gradient', 'solid'],
                gradient: { shadeIntensity: 1, opacityFrom: 0.35, opacityTo: 0.03, stops: [0, 90, 100] },
                opacity: [1, 0],
            },
            markers: { size: 4, strokeWidth: 0 },
            dataLabels: { enabled: false },
            legend: { show: false },
            grid: { borderColor: '#f0f0f0' },
            xaxis: { categories, labels: { style: { fontSize: '12px' } } },
            yaxis: {
                min: 0,
                labels: { formatter: (v) => get2DecimalPointsWithCommas(v, 0) },
            },
            annotations: { points: annotationPoints },
            tooltip: {
                shared: true,
                intersect: false,
                y: { formatter: (v) => v != null ? `${get2DecimalPointsWithCommas(v, 0)} ${t('unit.baht', t('baht'))}` : '-' },
            },
        };
        const series = [
            { name: `${t('dashboard.label.year', t('year'))} ${salaryYear}`, data: curSeries },
            { name: `${t('dashboard.label.year', t('year'))} ${salaryYear - 1} (${t('dashboard.label.last_year', t('lastYear'))})`, data: prevSeries },
        ];

        return (
            <Flex vertical gap={16}>
                <Flex gap={16}>
                    <Flex align="center" gap={6}>
                        <span style={{ width: 14, height: 3, background: '#3B82F6', display: 'inline-block' }} />
                        <span style={{ fontSize: 13 }}>{t('dashboard.label.year', t('year'))} {salaryYear}</span>
                    </Flex>
                    <Flex align="center" gap={6}>
                        <span style={{ width: 14, height: 0, borderTop: '3px dashed #FF5A9E', display: 'inline-block' }} />
                        <span style={{ fontSize: 13 }}>{t('dashboard.label.year', t('year'))} {salaryYear - 1} ({t('dashboard.label.last_year', t('lastYear'))})</span>
                    </Flex>
                </Flex>

                <Chart options={options} series={series} type="area" height={280} />

                <Card
                    bodyStyle={{ padding: '16px 24px' }}
                    style={{
                        background: '#FCFCFC',
                        border: '1px solid #E5E7EB',
                        boxShadow: 'none',
                    }}
                >
                    <Row gutter={[12, 12]}>
                        <Col
                            flex="1"
                            style={{ borderRight: '1px solid #E5E7EB', paddingRight: 16 }}
                        >
                            <Flex align="center" gap={8}>
                                <Flex justify="center" align="center" style={{ width: 40, height: 40, borderRadius: '50%', background: '#ffebeb', color: '#FF5858', fontSize: 18 }}>
                                    <WalletOutlined />
                                </Flex>
                                <Flex vertical>
                                    <span style={{ fontSize: 12, color: '#8c8c8c' }}>{t('dashboard.label.total_expense', t('totalExpense'))}</span>
                                    <span style={{ fontSize: 18, fontWeight: 700, color: '#FF5858' }}>
                                        {get2DecimalPointsWithCommas(totalExpense)} <span style={{ fontSize: 12, fontWeight: 400, color: '#263238' }}></span>
                                    </span>
                                    <span>{t('unit.baht_per_person', t('baht'))}</span>
                                </Flex>
                            </Flex>
                        </Col>

                        <Col
                            flex="1"
                            style={{ borderRight: '1px solid #E5E7EB', paddingLeft: 16, paddingRight: 16 }}
                        >
                            <Flex align="center" gap={8}>
                                <Flex justify="center" align="center" style={{ width: 40, height: 40, borderRadius: '50%', background: '#E9F3FF', color: '#3B82F6', fontSize: 18 }}>
                                    <WalletOutlined />
                                </Flex>
                                <Flex vertical>
                                    <span style={{ fontSize: 12, color: '#8c8c8c' }}>{t('dashboard.label.avg_salary_per_person', t('averageSalary'))}</span>
                                    <span style={{ fontSize: 18, fontWeight: 700, color: '#3B82F6' }}>
                                        {get2DecimalPointsWithCommas(salaryStats.avgPerPerson)} <span style={{ fontSize: 12, fontWeight: 400, color: '#263238' }}></span>
                                    </span>
                                    <span>{t('unit.baht_per_person', t('baht'))}</span>
                                </Flex>
                            </Flex>
                        </Col>

                        <Col
                            flex="1"
                            style={{ paddingLeft: 16 }}
                        >
                            <Flex align="center" gap={8}>
                                <Flex justify="center" align="center" style={{ width: 40, height: 40, borderRadius: '50%', background: '#E9F7EF', color: '#2bbf47', fontSize: 18, flexShrink: 0, marginTop: -10 }}>
                                    {salaryStats.pctChange >= 0 ? <RiseOutlined /> : <FallOutlined />}
                                </Flex>
                                <Flex vertical>
                                    <span style={{ fontSize: 12, color: '#8c8c8c' }}>{t('dashboard.label.vs_last_year_period', t('comparedSamePeriodLastYear'))}</span>
                                    <span style={{ fontSize: 16, fontWeight: 700, color: salaryStats.pctChange >= 0 ? '#2bbf47' : '#FF5858' }}>
                                        {salaryStats.pctChange >= 0 ? '↑' : '↓'} {Math.abs(salaryStats.pctChange).toFixed(2)}%{' '}
                                        <span style={{ fontSize: 12, color: '#8c8c8c', fontWeight: 400 }}>
                                            ({salaryStats.diffAmount >= 0 ? '+' : ''}{get2DecimalPointsWithCommas(salaryStats.diffAmount)} {t('unit.baht', t('baht'))})
                                        </span>
                                        <span></span>
                                    </span>
                                </Flex>
                            </Flex>
                        </Col>
                    </Row>
                </Card>
            </Flex>
        );
    };
    function DonutWithLegend({ items, total, totalLabel, t, i18n, onClickCustomer }) {
        const colors = items.map((o) => o.color);
        const series = items.map((o) => Number(o.count || 0));
        const labels = items.map((o) => (i18n.language === 'en' ? o.nameEn : o.name));

        const options = {
            chart: { type: 'donut' },
            labels,
            colors,
            dataLabels: { enabled: false },
            legend: { show: false },
            stroke: {
                show: true,
                width: 4,
                colors: ['#fff'],
            },
            plotOptions: { pie: { donut: { size: '75%', labels: { show: false } } } },
            tooltip: { enabled: true },
        };


        return (
            <Flex align="flex-start" gap={20}>
                <div style={{ position: 'relative', width: 170, height: 170, flexShrink: 0 }}>
                    <Chart options={options} series={series} type="donut" width={170} height={170} />
                    <div
                        style={{
                            position: 'absolute',
                            top: '40%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            textAlign: 'center',
                            pointerEvents: 'none',
                            width: 84,
                            lineHeight: 1.2,
                        }}
                    >
                        <div style={{ fontSize: 10, color: '#8c8c8c' }}>{totalLabel}</div>
                        <div style={{ fontSize: 20, fontWeight: 700, margin: '2px 0' }}>{total}</div>
                        <div style={{ fontSize: 10, color: '#8c8c8c' }}>{t('unit.person', t('person'))}</div>
                    </div>
                </div>
                <Flex vertical gap={10} justify="center" style={{ minHeight: 170 }}>
                    {items.map((o, i) => {
                        const percent = total ? ((o.count / total) * 100).toFixed(2) : 0;
                        const label = i18n.language === 'en' ? o.nameEn : o.name;
                        return (
                            <Flex key={i} align="center" gap={8} style={{ cursor: onClickCustomer ? 'pointer' : 'default' }} onClick={() => onClickCustomer?.(o, i)}>
                                <span
                                    style={{
                                        width: 10,
                                        height: 10,
                                        borderRadius: '50%',
                                        background: o.color,
                                        display: 'inline-block',
                                        flexShrink: 0,
                                    }}
                                />
                                <span style={{ fontSize: 13 }}>
                                    {label} {o.count} {t('unit.person', t('person'))}{' '}
                                    <span style={{ color: '#8c8c8c' }}>({percent}%)</span>
                                </span>
                            </Flex>
                        );
                    })}
                </Flex>
            </Flex>
        );
    }
    function AgeDonutWithLegend({ items, total, totalLabel, t, i18n, onClickCustomer }) {
        const colors = items.map((o) => o.color);
        const series = items.map((o) => Number(o.count || 0));
        const labels = items.map((o) => (i18n.language === 'en' ? o.nameEn : o.name));

        const options = {
            chart: { type: 'donut' },
            labels,
            colors,
            dataLabels: { enabled: false },
            legend: { show: false },
            stroke: { show: true, width: 4, colors: ['#fff'] },
            plotOptions: { pie: { donut: { size: '75%', labels: { show: false } } } },
            tooltip: { enabled: true },
        };

        return (
            <Flex vertical align="center" gap={4} style={{ width: '100%' }}>
                <div style={{ position: 'relative', width: 170, height: 170, flexShrink: 0 }}>
                    <Chart options={options} series={series} type="donut" width={170} height={170} />
                    <div
                        style={{
                            position: 'absolute',
                            top: '40%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            textAlign: 'center',
                            pointerEvents: 'none',
                            width: 84,
                            lineHeight: 1.2,
                        }}
                    >
                        <div style={{ fontSize: 10, color: '#8c8c8c' }}>{totalLabel}</div>
                        <div style={{ fontSize: 20, fontWeight: 700, margin: '2px 0' }}>{total}</div>
                        <div style={{ fontSize: 10, color: '#8c8c8c' }}>{t('unit.person', t('person'))}</div>
                    </div>
                </div>

                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                        columnGap: 12,
                        rowGap: 16,
                        width: '100%',
                        maxWidth: '100%',
                        boxSizing: 'border-box',
                        paddingLeft: 35,
                    }}
                >
                    {items.map((o, i) => {
                        const percent = total ? ((o.count / total) * 100).toFixed(2) : 0;
                        const label = i18n.language === 'en' ? o.nameEn : o.name;
                        return (
                            <Flex
                                key={i}
                                align="flex-start"
                                gap={6}
                                style={{ cursor: onClickCustomer ? 'pointer' : 'default', minWidth: 0 }}
                                onClick={() => onClickCustomer?.(o, i)}
                            >
                                <span
                                    style={{
                                        width: 10,
                                        height: 10,
                                        borderRadius: '50%',
                                        background: o.color,
                                        display: 'inline-block',
                                        flexShrink: 0,
                                        marginTop: 4,
                                    }}
                                />
                                <Flex vertical style={{ minWidth: 0, width: '100%' }}>
                                    <span
                                        style={{
                                            fontSize: 12,
                                            lineHeight: 1.4,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}
                                        title={label}
                                    >
                                        {label}
                                    </span>
                                    <span style={{ fontSize: 12, lineHeight: 1.4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {o.count} {t('unit.person', t('person'))}{' '}
                                        <span style={{ color: '#8c8c8c' }}>({percent}%)</span>
                                    </span>
                                </Flex>
                            </Flex>
                        );
                    })}
                </div>
            </Flex>
        );
    }
    const PieChart = () => {
        const total = dataPie?.reduce((p, c) => p + Number(c.count || 0), 0) ?? 0;
        return (
            <DonutWithLegend
                items={dataPieMapped}
                total={total}
                totalLabel={t('dashboard.label.total_employee', t('totalEmployee'))}
                t={t}
                i18n={i18n}
                onClickCustomer={(item) => onClickCustomer({ series: "genderId", value: item.name })}
            />
        );
    };



    const GEN_MAPPING = {
        'ต่ำกว่า 20 ปี': 'Gen Z',
        '20 - 29 ปี': 'Gen Z',
        '30 - 39 ปี': 'Gen Y',
        '40 - 49 ปี': 'Gen Y',
        '50 ปีขึ้นไป': 'Gen X',
    };
    const GEN_COLORS = {
        'Gen Z': '#3B82F6',
        'Gen Y': '#FFA152',
        'Gen X': '#FF50AD',
    };

    const dataGeneration = useMemo(() => {
        const genCounts = { 'Gen Z': 0, 'Gen Y': 0, 'Gen X': 0 };
        dataAgeRange.forEach((o) => {
            const gen = GEN_MAPPING[o.label];
            if (gen) genCounts[gen] += Number(o.count || 0);
        });
        const total = Object.values(genCounts).reduce((a, b) => a + b, 0);
        return ['Gen Z', 'Gen Y', 'Gen X']
            .map((gen) => ({
                gen,
                name: gen,
                nameEn: gen,
                count: genCounts[gen],
                color: GEN_COLORS[gen],
                percent: total ? Math.round((genCounts[gen] / total) * 100) : 0,
            }))
            .filter((o) => o.count > 0);
    }, [dataAgeRange]);
    const fnLayOut = (val, opts) => {
        return i18n.language == "en" ? `${val} employee ${opts.w.globals.series[opts.seriesIndex]} persons` : `พนักงาน${val} ${opts.w.globals.series[opts.seriesIndex]} คน`
    }

    // const PieChartType = () => {
    //     const [chartOptions, setChartOptions] = useState({});
    //     const [chartSeries, setChartSeries] = useState([]);
    //     const typeColors = ["#FFA152", "#24E08F", "#AF58FF", "#FF50AD", "#47B2FF", "#FF5858", "#FFD65C"];

    //     const total = dataPieType?.reduce((p, c) => p + Number(c.count || 0), 0) ?? 0;

    //     const options = {
    //         series: chartSeries,
    //         chart: {
    //             type: 'donut',
    //             toolbar: { show: true, tools: { download: true } },
    //             events: {
    //                 dataPointSelection: (event, chartContext, config) => {
    //                     if (config.dataPointIndex === -1 || config.seriesIndex === -1) return;
    //                     const item = {
    //                         series: "listEmployeeTypeId",
    //                         value: [dataPieType?.[config.dataPointIndex]?.id],
    //                     };
    //                     setTimeout(() => {
    //                         if (window.ApexCharts) {
    //                             try {
    //                                 window.ApexCharts.exec("pieChart", "destroy");
    //                                 onClickCustomer(item);
    //                             } catch (e) { console.warn("Chart destroy error:", e); }
    //                         }
    //                     }, 0);
    //                 }
    //             }
    //         },
    //         labels: dataPieType?.map(o => {
    //             const label = i18n.language === 'th' ? o.type : o.typeEn;
    //             return label === 'null' ? t('common.notSpecified') : label ?? t('common.notSpecified');
    //         }),
    //         dataLabels: { enabled: false },
    //         colors: typeColors,
    //         legend: { show: false },
    //         stroke: { width: 0 },
    //         plotOptions: { pie: { donut: { size: '70%' } } },
    //         tooltip: { enabled: true },
    //     };

    //     useEffect(() => {
    //         setChartOptions(options);
    //         setChartSeries(dataPieType.map(o => o.count));
    //     }, [dataPieType]);

    //     return (
    //         <Flex align="center" gap={16}>
    //             <div style={{ position: 'relative', width: 150, flexShrink: 0 }}>
    //                 <Chart options={chartOptions} series={chartSeries} type="donut" width={150} height={150} />
    //                 <div style={{
    //                     position: 'absolute', top: '50%', left: '50%',
    //                     transform: 'translate(-50%, -50%)', textAlign: 'center', pointerEvents: 'none'
    //                 }}>
    //                 </div>
    //             </div>
    //             <Flex vertical gap={10}>
    //                 {dataPieType?.map((o, i) => {
    //                     const percent = total ? ((o.count / total) * 100).toFixed(2) : 0;
    //                     const label = i18n.language === 'th' ? o.type : o.typeEn;
    //                     const displayLabel = label === 'null' ? t('common.notSpecified') : label ?? t('common.notSpecified');
    //                     return (
    //                         <Flex key={i} align="center" gap={8}>
    //                             <span style={{ width: 10, height: 10, borderRadius: '50%', background: typeColors[i % typeColors.length], display: 'inline-block', flexShrink: 0 }} />
    //                             <span style={{ fontSize: 13 }}>
    //                                 {displayLabel} {o.count} {t('unit.person', t('person'))}{' '}
    //                                 <span style={{ color: '#8c8c8c' }}>({percent}%)</span>
    //                             </span>
    //                         </Flex>
    //                     );
    //                 })}
    //             </Flex>
    //         </Flex>
    //     );
    // };

    const downloadHrDashboardExcel = useCallback(async () => {
        try {
            dispatch(setIsLoading(true));
            const response = await exportHrDashboardExcel({
                companyId: currentCompany,
                actionId: currentEmployee,
                employeeId: currentEmployee,
                salaryPeriodMonths,
                salaryYear,
                turnoverPeriodMonths,
                turnoverYear,
            });

            const blob = new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'HR_Dashboard_Report.xlsx';
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            Alert({ type: 'error', resultObject: error });
        } finally {
            dispatch(setIsLoading(false));
        }
    }, [currentCompany, currentEmployee, salaryPeriodMonths, salaryYear, turnoverPeriodMonths, turnoverYear, dispatch]);
    const ColumnChart = () => {

        const maxYValue = Math.max(...(dataColumnSalary?.map?.(o => o.count) ?? []));
        const maxY = maxYValue * 1.2;
        const options = {
            series: [{
                name: t("dashboard.label.amount"),
                data: dataColumnSalary?.map?.(o => o.count) ?? []
            }],
            chart: {
                type: 'bar',
                events: {
                    mounted: function () {
                        rotateDataLabels();
                    },
                    updated: function () {
                        rotateDataLabels();
                    },
                    dataPointSelection: (event, chartContext, config) => {

                        if (config.dataPointIndex === -1 || config.seriesIndex === -1) return;

                        const item = {
                            series: "listBranch",
                            value: [dataColumnSalary?.[config.dataPointIndex]?.branch?.branchId],
                        };

                        setTimeout(() => {
                            if (window.ApexCharts) {
                                try {
                                    window.ApexCharts.exec("pieChart", "destroy");
                                    onClickCustomer(item);
                                } catch (e) {
                                    console.warn("Chart destroy error:", e);
                                }
                            }
                        }, 0);
                    }
                }
            },
            plotOptions: {
                bar: {
                    columnWidth: '50%',
                    distributed: true,
                    borderRadius: 20,
                    borderRadiusApplication: 'end',
                    dataLabels: {
                        orientation: 'vertical',
                        position: 'top'
                    },
                }
            },
            dataLabels: {
                enabled: true,
                useHTML: false,
                formatter: function (val) {
                    return get2DecimalPointsWithCommas(val);
                },
                offsetY: 5,
                style: {
                    fontSize: '12px',
                    colors: ["#304758"]
                }
            },
            xaxis: {
                title: {
                    text: t("dashboard.label.workplace"),
                    offsetY: -10,
                },
                categories: dataColumnSalary?.map?.(o => i18n.language == 'en' ? o?.branch?.branchNameEn : o?.branch?.branchName) ?? [],
                position: 'bottom',
                labels: {
                    style: {
                        fontSize: '12px'
                    }
                }
            },
            yaxis: {
                max: maxY,
                min: 0,
                title: {
                    text: t("dashboard.label.amount")
                },
                labels: {
                    formatter: (value) => { return get2DecimalPointsWithCommas(value, 0) },
                },
            },
            theme: {
                palette: "palette1"
            }
        };

        const rotateDataLabels = useCallback(() => {
            setTimeout(() => {
                document.querySelectorAll(".apexcharts-data-labels").forEach(label => {
                    const transformAttr = label.getAttribute("transform");
                    if (transformAttr) {
                        const updatedTransform = transformAttr.replace(/rotate\(-?\d+/, "rotate(-45");
                        label.setAttribute("transform", updatedTransform);
                    }
                });
            }, 500);
        }, []);

        useEffect(() => {
            rotateDataLabels();
        }, [dataColumnSalary, rotateDataLabels]);

        return (
            <Flex vertical={true}>
                <Chart options={options} series={options.series} type={'bar'} height={380} />
                <Flex style={{
                    alignSelf: "flex-end",
                    backgroundColor: "#FFD65C",
                    borderRadius: "3rem",
                    padding: "4px 10px 4px 10px"
                }}
                    onClick={() => {
                        onClickCustomer()
                    }}
                >
                    {t("dashboard.label.net_expense")} {get2DecimalPointsWithCommas(dataColumnSalary?.map?.(o => Number(o?.count))?.filter?.(num => !isNaN(num))?.reduce?.((p, c) => p + c, 0))} {t("unit.baht")}
                </Flex>
            </Flex>
        );
    };

    // ===================== NEW: พนักงานเข้า-ออก (Turnover) chart =====================
    const TurnoverTrendChart = () => {
        const categories = dataTurnoverTrend.map(o => i18n.language === 'en' ? (o.monthLabelEn ?? o.monthLabel) : o.monthLabel);
        const newHireSeries = dataTurnoverTrend.map(o => Number(o.newHire ?? 0));
        const leaveSeries = dataTurnoverTrend.map(o => Number(o.leave ?? 0));
        const maxDataValue = Math.max(0, ...newHireSeries, ...leaveSeries);

        // ปัด step ให้เป็นเลขกลม เช่น 1, 2, 5, 10, 20, 25, 50, 100
        function niceStep(rawStep) {
            const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep || 1)));
            const residual = rawStep / magnitude;
            let niceResidual;
            if (residual <= 1) niceResidual = 1;
            else if (residual <= 2) niceResidual = 2;
            else if (residual <= 5) niceResidual = 5;
            else niceResidual = 10;
            return niceResidual * magnitude;
        }

        const desiredTicks = 5;
        const rawStep = Math.max(1, maxDataValue / desiredTicks);
        const step = niceStep(rawStep);
        const yAxisMax = step * desiredTicks;
        const yAxisTickAmount = desiredTicks;
        const options = {
            chart: { id: 'turnoverTrendChart', type: 'bar', toolbar: { show: false } },
            plotOptions: { bar: { columnWidth: '45%', borderRadius: 4, borderRadiusApplication: 'end' } },
            colors: ['#24E08F', '#FF5A9E'],
            dataLabels: { enabled: false },
            legend: { show: false },
            grid: { borderColor: '#f0f0f0' },
            xaxis: { categories, labels: { style: { fontSize: '12px' } } },
            yaxis: {
                min: 0,
                max: yAxisMax,
                tickAmount: yAxisTickAmount,
                labels: { formatter: (v) => Math.round(v) },
            },
            tooltip: {
                shared: true,
                intersect: false,
                custom: ({ dataPointIndex }) => {
                    const row = dataTurnoverTrend[dataPointIndex];
                    const monthLabel = i18n.language === 'en' ? (row?.monthLabelEn ?? row?.monthLabel) : row?.monthLabel;
                    return `
                        <div style="padding: 10px 14px; font-size: 13px;">
                            <div style="font-weight: 600; margin-bottom: 6px;">${monthLabel}</div>
                            <div style="display:flex; align-items:center; gap:6px; margin-bottom:4px;">
                                <span style="width:8px;height:8px;border-radius:50%;background:#24E08F;display:inline-block;"></span>
                                ${t('dashboard.label.new_hire', t('newHire'))}: <b>${row?.newHire ?? 0}</b>
                            </div>
                            <div style="display:flex; align-items:center; gap:6px; margin-bottom:4px;">
                                <span style="width:8px;height:8px;border-radius:50%;background:#FF5A9E;display:inline-block;"></span>
                                ${t('dashboard.label.leave', t('employeeLeave'))}: <b>${row?.leave ?? 0}</b>
                            </div>
                            <div style="border-top: 1px solid #f0f0f0; margin-top: 6px; padding-top: 6px;">
                                ${t('dashboard.label.total_employee', t('totalEmployee'))}: <b>${row?.totalActive ?? 0}</b> ${t('unit.person', t('person'))}
                            </div>
                        </div>
                    `;
                }
            },
        };
        const series = [
            { name: t('dashboard.label.new_hire', t('newHire')), data: newHireSeries },
            { name: t('dashboard.label.leave', t('employeeLeave')), data: leaveSeries },
        ];

        return (
            <Flex vertical gap={16}>
                <Chart options={options} series={series} type="bar" height={280} />
                <Flex gap={16}>
                    <Flex align="center" gap={6}>
                        <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#24E08F', display: 'inline-block' }} />
                        <span style={{ fontSize: 13 }}>{t('dashboard.label.new_hire', t('newHire'))}</span>
                    </Flex>
                    <Flex align="center" gap={6}>
                        <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#FF5A9E', display: 'inline-block' }} />
                        <span style={{ fontSize: 13 }}>{t('dashboard.label.leave', t('employeeLeave'))}</span>
                    </Flex>
                </Flex>
            </Flex>
        );
    };
    const dataTenure = useMemo(() => {
        const now = dayjs();

        const groups = [
            { key: 'underOneYear', count: 0, color: "#6C63FF" },
            { key: 'oneToFiveYears', count: 0, color: "#22A6F2" },
            { key: 'sixToTenYearsOld', count: 0, color: "#20C997" },
            { key: 'moreThanTenYears', count: 0, color: "#FFA726" },
        ];

        (employeeGroupInfoByCompany?.dateofwork ?? [])
            .filter(isActiveEmployee)
            .forEach((item) => {
                if (!item.employmentDate) return;

                const years = now.diff(dayjs(item.employmentDate), "year", true);

                if (years < 1) {
                    groups[0].count++;
                } else if (years <= 5) {
                    groups[1].count++;
                } else if (years <= 10) {
                    groups[2].count++;
                } else {
                    groups[3].count++;
                }
            });

        return groups.map(o => ({
            ...o,
            name: t(o.key),
            nameEn: t(o.key),
        }));
    }, [employeeGroupInfoByCompany?.dateofwork, isActiveEmployee, t]);
    // const TenureCard = () => {
    //     const total = dataTenure.reduce((sum, item) => sum + item.count, 0);

    //     return (
    //         <Flex vertical gap={14}>
    //             {dataTenure.map((item) => (
    //                 <Flex
    //                     key={item.label}
    //                     align="center"
    //                     justify="space-between"
    //                     gap={12}
    //                 >
    //                     <div style={{ width: 90, fontSize: 14 }}>
    //                         {t(item.key)}
    //                     </div>

    //                     <div
    //                         style={{
    //                             flex: 1,
    //                             height: 10,
    //                             background: "#ECECEC",
    //                             borderRadius: 999,
    //                             overflow: "hidden",
    //                         }}
    //                     >
    //                         <div
    //                             style={{
    //                                 width: `${total ? (item.count / total) * 100 : 0}%`,
    //                                 height: "100%",
    //                                 background: item.color,
    //                                 borderRadius: 999,
    //                             }}
    //                         />
    //                     </div>

    //                     <div
    //                         style={{
    //                             width: 100, fontSize: 13, textAlign: 'right', flexShrink: 0
    //                         }}
    //                     >
    //                         {item.count} {t('unit.person', t('person'))}
    //                     </div>
    //                 </Flex>
    //             ))}
    //         </Flex>
    //     );
    // };
    // ===================== NEW: พนักงานที่เพิ่งเข้าใหม่ list =====================
    const RecentHiresList = () => {
        function formatDate(dateString) {
            if (!dateString) return '-';
            const date = dayjs(dateString);
            return i18n.language === 'en'
                ? date.format('DD/MM/YYYY')
                : date.format(`DD/MM/${date.year() + 543}`);
        }

        return (
            <Flex vertical gap={16}>
                {dataRecentHires.slice(0, 5).map((o, i) => (
                    <Flex
                        key={o.id ?? i}
                        align="center"
                        gap={12}
                        style={{ cursor: 'pointer' }}
                        onClick={() => onClickCustomer({ series: 'employeeId', value: [o.id] })}
                    >
                        <Avatar
                            icon={<UserOutlined />}
                            size={40}
                            style={{ flexShrink: 0, backgroundColor: '#E9F3FF', color: '#47B2FF' }}
                        />
                        <Flex vertical style={{ flex: 1, minWidth: 0 }}>
                            <span style={{ fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {i18n.language === 'en' ? `${o.nameEn ?? ''} ${o.surNameEn ?? ''}` : `${o.name ?? ''} ${o.surName ?? ''}`}
                            </span>
                            <span style={{ fontSize: 12, color: '#8c8c8c' }}>
                                {t('dashboard.label.work_start_date', 'วันที่เริ่มงาน')}: {formatDate(o.employmentDate)}
                            </span>
                        </Flex>
                    </Flex>
                ))}
                {dataRecentHires.length === 0 && (
                    <span style={{ fontSize: 13, color: '#8c8c8c', textAlign: 'center' }}>
                        {t('common.noData', 'ไม่มีข้อมูล')}
                    </span>
                )}
            </Flex>
        );
    };

    const getEmployeeGroupByGenderFn = useCallback(
        async (params = {}) => {
            try {
                dispatch(setIsLoading(true));
                const response = await getEmployeeGroupByGender({
                    ...params,
                    companyId: currentCompany
                });
                dispatch(setEmployeeGroupByGender(response.data.employeeGroupByGender || []));

            } catch (error) {
                console.log("error", error);
            } finally {
                dispatch(setIsLoading(false));
            }
        },
        [currentCompany, dispatch]
    );

    const getEmployeeSalaryByBranchFn = useCallback(
        async (params = {}) => {
            try {
                dispatch(setIsLoading(true));
                const response = await getEmployeeSalaryByBranch({
                    ...params,
                    companyId: currentCompany
                });
                dispatch(setEmployeeSalaryByBranch(response.data.content || []));
            } catch (error) {
                console.log("error", error);
            } finally {
                dispatch(setIsLoading(false));
            }
        },
        [currentCompany, dispatch]
    );

    const getEmployeeGroupByTypeFn = useCallback(
        async (params = {}) => {
            try {
                dispatch(setIsLoading(true));
                const response = await getEmployeeGroupByType({
                    ...params,
                    companyId: currentCompany
                });
                dispatch(setEmployeeGroupByType(response.data?.content || []));

            } catch (error) {
                console.log("error", error);
            } finally {
                dispatch(setIsLoading(false));
            }
        },
        [currentCompany, dispatch]
    );
    const getEmployeeGroupInfoByCompanyFn = useCallback(
        async (params = {}) => {
            try {
                dispatch(setIsLoading(true));
                const response = await getEmployeeGroupInfoByCompany({
                    ...params,
                    companyId: currentCompany
                });
                dispatch(setEmployeeGroupInfoByCompany(response.data.content?.[0] || null));
            } catch (error) {
                console.log("error", error);
            } finally {
                dispatch(setIsLoading(false));
            }
        },
        [currentCompany, dispatch]
    );

    useEffect(() => {
        if (currentCompany) {
            getEmployeeGroupByGenderFn()
            getEmployeeGroupByTypeFn()
            getEmployeeSalaryByBranchFn()
            getEmployeeGroupInfoByCompanyFn()
        }
    }, [currentCompany]);
    function BreakdownBarList({ items, onClickCustomer }) {
        const total = items.reduce((p, c) => p + c.count, 0);

        const ROW_HEIGHT = 24;
        const ROW_GAP = 14;
        const VISIBLE_ROWS = 5;
        const maxListHeight = VISIBLE_ROWS * ROW_HEIGHT + (VISIBLE_ROWS - 1) * ROW_GAP;
        const scrollable = items.length > VISIBLE_ROWS;

        return (
            <>
                {scrollable && (
                    <style>{`
                            .breakdown-scroll::-webkit-scrollbar {
                                width: 6px;
                            }
                            .breakdown-scroll::-webkit-scrollbar-track {
                                background: #f0f0f0;
                                border-radius: 10px;
                            }
                            .breakdown-scroll::-webkit-scrollbar-thumb {
                                background: #c0c0c0;
                                border-radius: 10px;
                            }
                            .breakdown-scroll::-webkit-scrollbar-thumb:hover {
                                background: #a0a0a0;
                            }
                        `}</style>
                )}
                <div
                    className={scrollable ? 'breakdown-scroll' : ''}
                    style={{
                        maxHeight: scrollable ? maxListHeight : 'none',
                        overflowY: scrollable ? 'auto' : 'visible',
                        paddingRight: scrollable ? 10 : 0,
                        scrollbarWidth: scrollable ? 'thin' : 'auto',
                        scrollbarColor: scrollable ? '#c0c0c0 #f0f0f0' : 'auto',
                    }}
                >
                    <Flex vertical gap={ROW_GAP}>
                        {items.map((o, i) => {
                            const label = i18n.language === 'en' ? o.nameEn : o.name;
                            const widthPercent = total ? (o.count / total) * 100 : 0;
                            const percent = total ? ((o.count / total) * 100).toFixed(2) : 0;
                            return (
                                <Flex key={i} align="center" gap={10} style={{ cursor: onClickCustomer ? 'pointer' : 'default' }} onClick={() => onClickCustomer?.(o, i)} >
                                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: o.color, display: 'inline-block', flexShrink: 0 }} />
                                    <span style={{ width: 140, fontSize: 13, flexShrink: 0 }} title={label}>{label}</span>
                                    <div style={{ flex: '0 1 45%', maxWidth: '45%', background: '#f0f0f0', borderRadius: 20, height: 10, marginLeft: -5 }}>
                                        <div style={{
                                            width: `${widthPercent}%`,
                                            background: o.color,
                                            height: '100%',
                                            borderRadius: 20,
                                            transition: 'width 0.3s',
                                        }} />
                                    </div>
                                    <span style={{ flex: 1, fontSize: 13, textAlign: 'center', whiteSpace: 'nowrap' }}>
                                        {o.count} {t('unit.person', t('person'))} <span style={{ color: '#8c8c8c' }}>({percent}%)</span>
                                    </span>
                                </Flex>
                            );
                        })}
                    </Flex>
                </div>
            </>
        );
    }
    const TenureCard = () => {
        const totalTenure = dataTenure?.reduce((p, c) => p + Number(c.count || 0), 0) ?? 0;
        return (
            <DonutWithLegend
                items={dataTenure}
                total={totalTenure}
                totalLabel={t('dashboard.label.total_employee', t('totalEmployee'))}
                t={t}
                i18n={i18n}
            />
        );
    };
    const PieChartType = () => <BreakdownBarList
        items={dataPieTypeMapped}
        onClickCustomer={(item) => onClickCustomer({ series: "listEmployeeTypeId", value: [item.id] })}
    />;

    const BranchBreakdownChart = () => <BreakdownBarList
        items={dataBranchBreakdown}
        onClickCustomer={(item) => onClickCustomer({ series: "listBranch", value: [item.id] })}
    />;

    const DepartmentBreakdownChart = () => <BreakdownBarList
        items={dataDepartmentBreakdown}
        onClickCustomer={(item) => onClickCustomer({ series: "listDepartmentId", value: [item.id] })}
    />;

    const NationalityChart = () => <BreakdownBarList items={dataNationality} />;

    return {
        PieChart,
        employeeGroupByGender,
        newEmployee,
        dataPieType,
        employeeSalaryByBranch,
        dataPie,
        NationalityChart,
        TenureCard,
        AgeRangeChart,
        dataPieTypeMapped,
        PieChartType,
        BranchBreakdownChart,
        DepartmentBreakdownChart,
        ColumnChart,
        pagination,
        setPagination,
        dataSource,
        onClickCustomer,
        employeeStatusTypeOptions,
        onClickNewHireCard,
        onClickResignedCard,
        TurnoverTrendChart,
        RecentHiresList,
        turnoverStats,
        dataRecentHires,
        turnoverPeriodMonths,
        setTurnoverPeriodMonths,
        turnoverYear,
        setTurnoverYear,
        turnoverYearOptions,

        SalaryTrendChart,
        salaryPeriodMonths,
        setSalaryPeriodMonths,
        salaryYear,
        setSalaryYear,
        DistributionList,
        downloadHrDashboardExcel,
    }


}

export default useDashboard